import EmptyState from '@/components/linear/empty-state';
import IssueRow from '@/components/linear/issue-row';
import StatusIcon from '@/components/linear/status-icon';
import { useWorkspaceChrome } from '@/components/linear/workspace-chrome';
import { groupIssues, type IssueGroupBy } from '@/lib/linear-utils';
import type { Issue, IssueStatus } from '@/types/issue';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Props {
  issues: Issue[];
  selectedId?: string | null;
  onSelect?: (issue: Issue) => void;
  groupBy?: IssueGroupBy;
}

export default function IssueList({ issues, selectedId, onSelect, groupBy = 'status' }: Props) {
  const { selectedIndex, registerItems, setSelectedIndex, items, setCreateIssueOpen } =
    useWorkspaceChrome();
  const groups = groupIssues(issues, groupBy);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    registerItems(
      issues.map((issue) => ({
        id: issue.id,
        identifier: issue.identifier,
        issue,
      }))
    );

    return () => registerItems([]);
  }, [issues, registerItems]);

  if (issues.length === 0) {
    return <EmptyState description="No issues." />;
  }

  return (
    <div>
      {groups.map((group) => {
        const isCollapsed = collapsed[group.key] === true;
        const status = group.key as IssueStatus;

        return (
          <section key={group.key}>
            <div className="ln-group-header">
              <button
                type="button"
                className="ln-group-toggle"
                onClick={() =>
                  setCollapsed((current) => ({
                    ...current,
                    [group.key]: !current[group.key],
                  }))
                }
              >
                {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                {groupBy === 'status' ? <StatusIcon status={status} /> : null}
                <span>{group.label}</span>
                <span className="ln-group-count">{group.issues.length}</span>
              </button>
              <button
                type="button"
                className="ln-group-add"
                aria-label={`Create ${group.label} issue`}
                onClick={() => setCreateIssueOpen(true)}
              >
                <Plus size={14} />
              </button>
            </div>
            {isCollapsed
              ? null
              : group.issues.map((issue) => {
                  const itemIndex = items.findIndex((item) => item.id === issue.id);
                  const selected = selectedId
                    ? selectedId === issue.id || selectedId === issue.identifier
                    : itemIndex === selectedIndex;

                  return (
                    <IssueRow
                      key={issue.id}
                      issue={issue}
                      selected={selected}
                      onSelect={(next) => {
                        if (itemIndex >= 0) setSelectedIndex(itemIndex);
                        if (onSelect) onSelect(next);
                      }}
                    />
                  );
                })}
          </section>
        );
      })}
    </div>
  );
}
